/**
 * Visitor Tracking System
 * Tracks visitors with cookies and provides analytics data
 */

export interface VisitorData {
  visitorId: string;
  sessionId: string;
  firstVisit: string;
  lastVisit: string;
  visitCount: number;
  pageViews: number;
  referrer: string;
  userAgent: string;
  language: string;
  timezone: string;
  screenResolution: string;
  currentPage: string;
  sessionStartTime: string;
  sessionDuration: number;
}

export interface PageView {
  page: string;
  timestamp: string;
  duration: number;
  referrer: string;
}

class VisitorTracker {
  private visitorId: string;
  private sessionId: string;
  private sessionStartTime: number;
  private currentPageStartTime: number;
  private pageViews: PageView[] = [];

  constructor() {
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionId = this.getOrCreateSessionId();
    this.sessionStartTime = Date.now();
    this.currentPageStartTime = Date.now();
    this.initializeTracking();
  }

  /**
   * Get or create a unique visitor ID (persistent across sessions)
   */
  private getOrCreateVisitorId(): string {
    let visitorId = this.getCookie('visitor_id');
    
    if (!visitorId) {
      visitorId = this.generateUniqueId('visitor');
      this.setCookie('visitor_id', visitorId, 365 * 24 * 60 * 60 * 1000); // 1 year
    }
    
    return visitorId;
  }

  /**
   * Get or create a session ID (expires when browser closes)
   */
  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('session_id');
    
    if (!sessionId) {
      sessionId = this.generateUniqueId('session');
      sessionStorage.setItem('session_id', sessionId);
    }
    
    return sessionId;
  }

  /**
   * Generate a unique ID with timestamp and random string
   */
  private generateUniqueId(prefix: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${prefix}_${timestamp}_${random}`;
  }

  /**
   * Set a cookie with expiration
   */
  private setCookie(name: string, value: string, maxAge: number): void {
    const expires = new Date(Date.now() + maxAge).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * Get a cookie value
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Initialize tracking data
   */
  private initializeTracking(): void {
    const now = new Date().toISOString();
    
    // Update visit count
    const visitCount = parseInt(this.getCookie('visit_count') || '0') + 1;
    this.setCookie('visit_count', visitCount.toString(), 365 * 24 * 60 * 60 * 1000);
    
    // Set first visit if not exists
    if (!this.getCookie('first_visit')) {
      this.setCookie('first_visit', now, 365 * 24 * 60 * 60 * 1000);
    }
    
    // Update last visit
    this.setCookie('last_visit', now, 365 * 24 * 60 * 60 * 1000);
    
    // Track page views count
    const pageViews = parseInt(this.getCookie('page_views') || '0') + 1;
    this.setCookie('page_views', pageViews.toString(), 365 * 24 * 60 * 60 * 1000);
  }

  /**
   * Track a page view
   */
  public trackPageView(page: string): void {
    const now = Date.now();
    const previousPageDuration = now - this.currentPageStartTime;
    
    // Save previous page duration if it's not the first page
    if (this.pageViews.length > 0) {
      const lastPageView = this.pageViews[this.pageViews.length - 1];
      lastPageView.duration = previousPageDuration;
    }
    
    // Add new page view
    const pageView: PageView = {
      page,
      timestamp: new Date().toISOString(),
      duration: 0, // Will be updated when user navigates away
      referrer: document.referrer || 'direct'
    };
    
    this.pageViews.push(pageView);
    this.currentPageStartTime = now;
    
    // Update page views count
    const pageViews = parseInt(this.getCookie('page_views') || '0') + 1;
    this.setCookie('page_views', pageViews.toString(), 365 * 24 * 60 * 60 * 1000);
    
    // Send tracking data to backend (optional)
    this.sendTrackingData();
  }

  /**
   * Get current visitor data
   */
  public getVisitorData(): VisitorData {
    const now = Date.now();
    const sessionDuration = now - this.sessionStartTime;
    
    return {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      firstVisit: this.getCookie('first_visit') || new Date().toISOString(),
      lastVisit: this.getCookie('last_visit') || new Date().toISOString(),
      visitCount: parseInt(this.getCookie('visit_count') || '1'),
      pageViews: parseInt(this.getCookie('page_views') || '1'),
      referrer: document.referrer || 'direct',
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${screen.width}x${screen.height}`,
      currentPage: window.location.pathname,
      sessionStartTime: new Date(this.sessionStartTime).toISOString(),
      sessionDuration: Math.round(sessionDuration / 1000) // in seconds
    };
  }

  /**
   * Get page views for current session
   */
  public getPageViews(): PageView[] {
    return [...this.pageViews];
  }

  /**
   * Send tracking data to backend (implement as needed)
   */
  private async sendTrackingData(): Promise<void> {
    try {
      const visitorData = this.getVisitorData();
      
      // Uncomment and modify this section to send data to your backend
      /*
      const response = await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...visitorData,
          pageViews: this.pageViews
        })
      });
      
      if (!response.ok) {
        console.warn('Failed to send tracking data');
      }
      */
      
      // For now, just log to console (remove in production)
      console.log('Visitor tracking data:', visitorData);
    } catch (error) {
      console.warn('Error sending tracking data:', error);
    }
  }

  /**
   * Track custom events
   */
  public trackEvent(eventName: string, eventData?: any): void {
    const event = {
      name: eventName,
      data: eventData,
      timestamp: new Date().toISOString(),
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      page: window.location.pathname
    };
    
    // Store in session storage for now
    const events = JSON.parse(sessionStorage.getItem('tracking_events') || '[]');
    events.push(event);
    sessionStorage.setItem('tracking_events', JSON.stringify(events));
    
    console.log('Event tracked:', event);
  }

  /**
   * Get all tracked events for current session
   */
  public getTrackedEvents(): any[] {
    return JSON.parse(sessionStorage.getItem('tracking_events') || '[]');
  }

  /**
   * Clear tracking data (for privacy compliance)
   */
  public clearTrackingData(): void {
    // Clear cookies
    document.cookie = 'visitor_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'visit_count=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'page_views=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'first_visit=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'last_visit=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Clear session storage
    sessionStorage.removeItem('session_id');
    sessionStorage.removeItem('tracking_events');
    
    console.log('Tracking data cleared');
  }
}

// Create singleton instance
export const visitorTracker = new VisitorTracker();

// Export utility functions
export const trackPageView = (page: string) => visitorTracker.trackPageView(page);
export const trackEvent = (eventName: string, eventData?: any) => visitorTracker.trackEvent(eventName, eventData);
export const getVisitorData = () => visitorTracker.getVisitorData();
export const getPageViews = () => visitorTracker.getPageViews();
export const getTrackedEvents = () => visitorTracker.getTrackedEvents();
export const clearTrackingData = () => visitorTracker.clearTrackingData();